import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API, unwrap, apiMessage, toast, mediaKind } from "./projetsCommon";

/** Galerie photos / vidéos d'un projet (documents GED rattachés). */
const ProjetGalerie = ({ projet, onCoverChange }) => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [thumbs, setThumbs] = useState({});
    const thumbsRef = useRef({});
    const [lightbox, setLightbox] = useState(null);

    const clearThumbs = () => {
        Object.values(thumbsRef.current).forEach((u) => window.URL.revokeObjectURL(u));
        thumbsRef.current = {};
        setThumbs({});
    };

    useEffect(() => {
        let cancelled = false;
        clearThumbs();
        setLoading(true);
        axiosInstance.get(API.projetDocuments(projet.id))
            .then((res) => {
                if (cancelled) return;
                const list = unwrap(res) || [];
                setDocs(list);
                list.filter((d) => mediaKind(d) === "image").forEach(async (d) => {
                    try {
                        const r = await axiosInstance.get(API.preview(d.id), { responseType: "blob" });
                        if (cancelled) return;
                        thumbsRef.current = { ...thumbsRef.current, [d.id]: window.URL.createObjectURL(r.data) };
                        setThumbs({ ...thumbsRef.current });
                    } catch { /* miniature indisponible */ }
                });
            })
            .catch((error) => toast("error", apiMessage(error, "Impossible de charger les documents")))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; clearThumbs(); };
    }, [projet.id]);

    const media = useMemo(() => docs.filter((d) => mediaKind(d)), [docs]);
    const others = useMemo(() => docs.filter((d) => !mediaKind(d)), [docs]);

    const download = async (doc) => {
        try {
            const res = await axiosInstance.get(API.download(doc.id), { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = doc.fileName || "document"; a.click();
            window.URL.revokeObjectURL(url);
        } catch { toast("error", "Téléchargement échoué"); }
    };

    const open = async (doc) => {
        const kind = mediaKind(doc);
        if (kind === "image" && thumbs[doc.id]) return setLightbox({ doc, kind, url: thumbs[doc.id], owned: false });
        setLightbox({ doc, kind, url: null, loading: true });
        try {
            const res = await axiosInstance.get(API.preview(doc.id), { responseType: "blob" });
            setLightbox({ doc, kind, url: window.URL.createObjectURL(res.data), owned: true });
        } catch { setLightbox(null); toast("error", "Aperçu indisponible"); }
    };
    const close = () => {
        if (lightbox?.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        setLightbox(null);
    };
    const move = (step) => {
        if (!lightbox || media.length < 2) return;
        const i = media.findIndex((d) => d.id === lightbox.doc.id);
        if (lightbox.owned && lightbox.url) window.URL.revokeObjectURL(lightbox.url);
        open(media[(i + step + media.length) % media.length]);
    };

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") move(1);
            if (e.key === "ArrowLeft") move(-1);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    });

    if (loading) return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
    if (docs.length === 0) return (
        <div className="text-center text-muted py-5">
            <i className="far fa-images fa-3x mb-3 d-block"></i>
            Aucun document. Rattachez des photos / vidéos depuis Archives → Documents → ⋮ → « Rattacher à un projet ».
        </div>
    );

    return (
        <>
            {media.length > 0 && (
                <div className="media-grid">
                    {media.map((d) => {
                        const kind = mediaKind(d);
                        return (
                            <div key={d.id} className="media-tile" onClick={() => open(d)} title={d.commentaire || d.fileName}>
                                {kind === "image" && thumbs[d.id]
                                    ? <img src={thumbs[d.id]} alt={d.fileName} loading="lazy" />
                                    : <div className="media-placeholder"><i className={`fas ${kind === "video" ? "fa-play-circle" : "fa-image"} fa-3x`}></i></div>}
                                {kind === "video" && <span className="media-badge"><i className="fas fa-video"></i></span>}
                                {projet.coverDocumentId === d.id && <span className="media-cover-badge"><i className="fas fa-star"></i> Couverture</span>}
                                <div className="media-caption">{d.commentaire || d.fileName}</div>
                            </div>
                        );
                    })}
                </div>
            )}
            {others.length > 0 && (
                <>
                    <h6 className="text-muted mt-3">Autres documents</h6>
                    <ul className="list-group">
                        {others.map((d) => (
                            <li key={d.id} className="list-group-item d-flex align-items-center">
                                <i className="fas fa-file-alt text-secondary mr-2"></i>
                                <span className="flex-grow-1 text-truncate">
                                    {d.fileName}{d.commentaire && <small className="d-block text-muted">{d.commentaire}</small>}
                                </span>
                                <button className="btn btn-sm btn-outline-primary" onClick={() => download(d)}><i className="fas fa-download"></i></button>
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {lightbox && (
                <div className="media-lightbox" onClick={close}>
                    <div className="media-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
                        <span className="text-truncate mr-3">
                            {lightbox.doc.fileName}
                            {lightbox.doc.commentaire && <small className="d-block text-white-50">{lightbox.doc.commentaire}</small>}
                        </span>
                        <div className="text-nowrap">
                            {lightbox.kind === "image" && onCoverChange && (
                                <button className="btn btn-sm btn-light mr-2" onClick={() => onCoverChange(lightbox.doc.id)}>
                                    <i className="fas fa-star mr-1"></i>Couverture
                                </button>
                            )}
                            <button className="btn btn-sm btn-light mr-2" onClick={() => download(lightbox.doc)}><i className="fas fa-download"></i></button>
                            <button className="btn btn-sm btn-light" onClick={close}><i className="fas fa-times"></i></button>
                        </div>
                    </div>
                    {media.length > 1 && (
                        <>
                            <button className="media-nav media-nav-left" onClick={(e) => { e.stopPropagation(); move(-1); }}><i className="fas fa-chevron-left"></i></button>
                            <button className="media-nav media-nav-right" onClick={(e) => { e.stopPropagation(); move(1); }}><i className="fas fa-chevron-right"></i></button>
                        </>
                    )}
                    <div className="media-lightbox-content" onClick={(e) => e.stopPropagation()}>
                        {lightbox.loading || !lightbox.url ? <i className="fas fa-spinner fa-spin fa-3x text-white"></i>
                            : lightbox.kind === "video" ? <video src={lightbox.url} controls autoPlay />
                                : <img src={lightbox.url} alt={lightbox.doc.fileName} />}
                    </div>
                </div>
            )}
        </>
    );
};

export default ProjetGalerie;
